// src/lib/supabaseQuery.ts
import { supabase } from '../supabase';
import { queryClient } from './queryClient';
import type { QueryKey } from '@tanstack/react-query';

/**
 * Wraps a Supabase query builder into a TanStack Query fetcher.
 *
 * The builder receives the supabase client and returns a Supabase query
 * builder (e.g. `db => db.from('users').select('*').eq('tenant_id', tid)`). // perf-sweep-ignore: documentation example, not executed
 *
 * Extracts data, throws on error. Does NOT auto-apply .limit() — the
 * caller is responsible for adding .limit() per AGENTS.md §1, because
 * some queries use .maybeSingle() or .single() where .limit() would
 * change semantics.
 *
 * @param builder - Receives supabase client, returns a query builder
 * @returns Async function for useQuery queryFn
 *
 * @example
 * const { data } = useQuery({
 *   queryKey: queryKeys.users.list(tenantId),
 *   queryFn: supabaseQuery(db => db.from('users').select('*') // perf-sweep-ignore: jsdoc example — .limit(50) on the next line
 *     .eq('tenant_id', tenantId).limit(50)),
 * });
 */
export function supabaseQuery<T = any>(
  builder: (_db: typeof supabase) => PromiseLike<{ data: T | null; error: Error | null }>
): () => Promise<T> {
  return async () => {
    const query = builder(supabase);
    const { data, error } = await query;
    if (error) throw error;
    return data as T;
  };
}

/**
 * Creates a TanStack Query v5 mutation options object for Supabase
 * insert/update/delete operations, with automatic cache invalidation
 * on success.
 *
 * In v5, useMutation's onSuccess receives (data, variables, onMutateResult)
 * — the queryClient is accessed via the mutation's context, not as a
 * third arg. Instead, we return the options and the caller passes
 * queryClient to useMutation which handles injection internally.
 *
 * @param builder - (supabase, variables) => Supabase query builder
 * @param invalidateKeys - Array of query key arrays to invalidate on success
 * @returns Mutation options for useMutation()
 *
 * @example
 * const mutation = useMutation(supabaseMutation(
 *   (db, { userId, status }) => db.from('hr_state') // perf-sweep-ignore: documentation example — .update() on the next line
 *     .update({ status }).eq('user_id', userId),
 *   { invalidateKeys: [['hr', 'state']] }
 * ));
 */
export function supabaseMutation<V = any, T = any>(
  builder: (_db: typeof supabase, _variables: V) => PromiseLike<{ data: T | null; error: Error | null }>,
  { invalidateKeys = [] }: { invalidateKeys?: QueryKey[] } = {}
) {
  return {
    mutationFn: async (variables: V): Promise<T> => {
      const { data, error } = await builder(supabase, variables);
      if (error) throw error;
      return data as T;
    },
    onSuccess: () => {
      invalidateKeys.forEach(key =>
        queryClient.invalidateQueries({ queryKey: key })
      );
    },
  };
}